import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Link} from "react-router-dom";
import type {Category, SubSubcategory, Subcategory} from "@corporatesclub/shared";
import {useI18n} from "./I18nProvider";
import {getCategoriesCached} from "@/lib/shopCache";
import {paths} from "@/lib/paths";
import {BEAUTY_DEPARTMENT_ID, HOME_DEPARTMENTS, type HomeDepartmentConfig} from "./homeDepartments";

type FlatCat = {id: number; nume: string; url: string; trail: string};

function flattenCategories(items: Category[]): FlatCat[] {
  const out: FlatCat[] = [];
  function walkSubs(subs: Subcategory[], trail: string) {
    for(const sc of subs) {
      out.push({id: sc.id, nume: sc.nume, url: sc.url, trail});
      if(sc.subsubcategorii?.length) walkSubSubs(sc.subsubcategorii, `${trail} › ${sc.nume}`);
    }
  }
  function walkSubSubs(subs: SubSubcategory[], trail: string) {
    for(const ssc of subs) {
      out.push({id: ssc.id, nume: ssc.nume, url: ssc.url, trail});
      if(ssc.subsubsubcategorii?.length) {
        for(const sssc of ssc.subsubsubcategorii) {
          out.push({id: sssc.id, nume: sssc.nume, url: sssc.url, trail: `${trail} › ${ssc.nume}`});
        }
      }
    }
  }
  for(const c of items) {
    out.push({id: c.id, nume: c.nume, url: c.url, trail: c.nume});
    if(c.subcategorii?.length) walkSubs(c.subcategorii, c.nume);
  }
  return out;
}

function norm(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

type Props = {
  open: boolean;
  onClose: () => void;
};

function DepartmentThumb({dept}: {dept: HomeDepartmentConfig}) {
  return <img src={dept.cardImage} alt="" className="cc-browseOverlay__deptThumb" loading="lazy" decoding="async" />;
}

export function CategoryBrowseOverlay({open, onClose}: Props) {
  const {t} = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [deptId, setDeptId] = useState(BEAUTY_DEPARTMENT_ID);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const activeDept = HOME_DEPARTMENTS.find((d) => d.id === deptId) || HOME_DEPARTMENTS[0];
  const flat = useMemo(() => flattenCategories(categories), [categories]);
  const activeCategory = categories.find((c) => c.id === categoryId) || categories[0] || null;
  const filtered = useMemo(() => {
    const s = norm(q.trim());
    if(!s) return [];
    return flat.filter((c) => norm(c.nume).includes(s) || norm(c.trail).includes(s)).slice(0, 40);
  }, [flat, q]);

  useEffect(() => {
    if(!open) return;
    getCategoriesCached().then((cats) => {
      setCategories(cats);
      setCategoryId((prev) => prev ?? cats[0]?.id ?? null);
    }).catch(() => {});
    setDeptId(BEAUTY_DEPARTMENT_ID);
    inputRef.current?.focus();
    document.body.classList.add("cc-browseOpen");
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("cc-browseOpen");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if(!open) {
      setQ("");
      setDeptId(BEAUTY_DEPARTMENT_ID);
      setCategoryId(null);
    }
  }, [open]);

  useEffect(() => {
    if(activeDept.id === BEAUTY_DEPARTMENT_ID && categories.length && categoryId === null) {
      setCategoryId(categories[0]?.id ?? null);
    }
  }, [activeDept.id, categories, categoryId]);

  if(!open) return null;

  const searching = q.trim().length > 0;
  const beautyActive = activeDept.id === BEAUTY_DEPARTMENT_ID && activeDept.status === "active";

  return createPortal(
    <div className="cc-browseOverlay cc-browseOverlay--departments" role="dialog" aria-modal="true" aria-label={t("Departments")}>
      <button type="button" className="cc-browseOverlay__backdrop" aria-label={t("Close search")} onClick={onClose} />
      <div className="cc-browseOverlay__panel">
        <div className="cc-browseOverlay__head">
          <h2 className="cc-browseOverlay__title">{t("Departments")}</h2>
          <div className="cc-browseOverlay__search">
            <i className="fa fa-search" aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              className="cc-browseOverlay__input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("Search departments categories...")}
              autoComplete="off"
              disabled={!beautyActive}
            />
            {q ? <button type="button" className="cc-browseOverlay__clear" onClick={() => setQ("")} aria-label={t("Remove")}><i className="fa fa-times" aria-hidden="true" /></button> : null}
          </div>
          <button type="button" className="cc-browseOverlay__close" onClick={onClose} aria-label={t("Close search")}>
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="cc-browseOverlay__body">
          {searching && beautyActive ? (
            <ul className="cc-browseOverlay__results">
              {filtered.length ? filtered.map((c) => (
                <li key={`${c.trail}-${c.id}-${c.url}`}>
                  <Link to={c.url} className="cc-browseOverlay__result" onClick={onClose}>
                    <span className="cc-browseOverlay__resultName">{c.nume}</span>
                    {c.trail ? <span className="cc-browseOverlay__resultTrail">{c.trail}</span> : null}
                  </Link>
                </li>
              )) : (
                <li className="cc-browseOverlay__empty">{t("No categories found")}</li>
              )}
            </ul>
          ) : (
            <div className="cc-browseOverlay__split">
              <ul className="cc-browseOverlay__depts">
                {HOME_DEPARTMENTS.map((dept) => {
                  const soon = dept.status === "coming_soon";
                  return (
                    <li key={dept.id}>
                      <button
                        type="button"
                        className={`cc-browseOverlay__dept${deptId === dept.id ? " isActive" : ""}${soon ? " isSoon" : ""}`}
                        onClick={() => {
                          setDeptId(dept.id);
                          if(dept.id === BEAUTY_DEPARTMENT_ID && categories[0]) setCategoryId(categories[0].id);
                        }}
                      >
                        <span className="cc-browseOverlay__deptCopy">
                          <span className="cc-browseOverlay__deptName">{t(dept.nameKey)}</span>
                          {soon ? <span className="cc-browseOverlay__deptBadge">{t("Home departments coming soon")}</span> : null}
                        </span>
                        <DepartmentThumb dept={dept} />
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="cc-browseOverlay__detail">
                {!beautyActive ? (
                  <div className="cc-browseOverlay__soonPanel">
                    <span className="cc-browseOverlay__soonBadge">{t("Home departments coming soon")}</span>
                    <h3 className="cc-browseOverlay__soonTitle">{t(activeDept.nameKey)}</h3>
                    <p className="cc-browseOverlay__soonText">{t(activeDept.descriptionKey)}</p>
                  </div>
                ) : activeCategory ? (
                  <div className="cc-browseOverlay__catSplit">
                    <ul className="cc-browseOverlay__catRoots">
                      {categories.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className={`cc-browseOverlay__catRoot${activeCategory.id === c.id ? " isActive" : ""}`}
                            onClick={() => setCategoryId(c.id)}
                          >
                            {c.nume}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="cc-browseOverlay__catDetail">
                      <div className="cc-browseOverlay__detailHead">
                        <Link to={activeCategory.url} className="cc-browseOverlay__detailTitle" onClick={onClose}>{activeCategory.nume}</Link>
                        <div className="cc-browseOverlay__detailActions">
                          <Link to={paths.departmentBeauty} className="cc-browseOverlay__detailHub" onClick={onClose}>{t("Beauty hub link")}</Link>
                          <Link to={activeCategory.url} className="cc-browseOverlay__detailAll" onClick={onClose}>{t("See all")}</Link>
                        </div>
                      </div>
                      <div className="cc-browseOverlay__groups">
                        {(activeCategory.subcategorii || []).length ? (
                          (activeCategory.subcategorii || []).map((sc) => (
                            <div key={sc.id} className="cc-browseOverlay__group">
                              <Link to={sc.url} className="cc-browseOverlay__groupTitle" onClick={onClose}>{sc.nume}</Link>
                              {sc.subsubcategorii?.length ? (
                                <ul className="cc-browseOverlay__groupLinks">
                                  {sc.subsubcategorii.slice(0, 8).map((ssc) => (
                                    <li key={ssc.id}><Link to={ssc.url} onClick={onClose}>{ssc.nume}</Link></li>
                                  ))}
                                  {sc.subsubcategorii.length > 8 ? (
                                    <li><Link to={sc.url} className="cc-browseOverlay__more" onClick={onClose}>+{sc.subsubcategorii.length - 8} {t("See all")}</Link></li>
                                  ) : null}
                                </ul>
                              ) : null}
                            </div>
                          ))
                        ) : (
                          <div className="cc-browseOverlay__group">
                            <Link to={activeCategory.url} className="cc-browseOverlay__groupTitle" onClick={onClose}>{activeCategory.nume}</Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function openCategoryBrowse(): void {
  window.dispatchEvent(new CustomEvent("cc-open-category-browse"));
}

export const openDepartmentBrowse = openCategoryBrowse;
